import { useMemo } from 'react';
import { useListInvestments } from '@/lib/api/investments.ts';
import { useGetMe } from '@/lib/api/me.ts';
import { useGetBillingSubscription } from '@/lib/api/billing.ts';
import { getInvestorInvestmentAccessState } from './investment-access.ts';
import { useGetInvestorPreferences } from '@/lib/api/investor-preferences.ts';
import { buildUpgradeWallPlansPath, mapInvestmentCountToUpgradeWallCount } from './upgrade-wall.ts';

export const useInvestorInvestmentAccess = () => {
  const { data: user, isLoading: isLoadingUser } = useGetMe();
  const { data: billingSubscription } = useGetBillingSubscription({
    enabled: user?.role === 'investor',
  });
  const { data: investorPreferences } = useGetInvestorPreferences({
    enabled: user?.role === 'investor',
  });
  const { data: investments, isLoading: isLoadingInvestments } = useListInvestments<'investor'>({
    enabled: user?.role === 'investor',
  });

  const accessState = useMemo(
    () =>
      getInvestorInvestmentAccessState({
        user,
        billingSubscription,
        investments,
      }),
    [billingSubscription, investments, user]
  );

  const upgradeWallInvestmentCount = mapInvestmentCountToUpgradeWallCount(
    investorPreferences?.investmentCount
  );

  return {
    user,
    billingSubscription: billingSubscription,
    investments,
    isLoading: isLoadingUser || (user?.role === 'investor' && isLoadingInvestments),
    upgradeWallInvestmentCount,
    upgradeWallPlansPath: buildUpgradeWallPlansPath(upgradeWallInvestmentCount),
    isLockedInvestment: (investmentId?: string) =>
      Boolean(investmentId && accessState.lockedInvestmentIds.includes(investmentId)),
    ...accessState,
  };
};
